import {
  BomTypeItem,
  BomLibraryItem,
  BomTypeGroupItem,
  BomV2Ref,
  CategoryV2Item,
  BomLibraryCardItem,
  BomLibraryFilterOptions,
  BomLibraryGroupItem,
  BomLibraryDetailItem,
} from '../types';

/**
 * One page of a paginated list. `append` is set for page 2 and beyond so the
 * reducer grows the loaded set instead of replacing it.
 */
export interface BomV2PagePayload<TItem> {
  items: TItem[];
  totalCount: number;
  append: boolean;
}

export const BOM_V2_ACTION_TYPES = {
  SET_LOADING_BOM_TYPES: 'BOM_V2/SET_LOADING_BOM_TYPES',
  SET_BOM_TYPES: 'BOM_V2/SET_BOM_TYPES',
  SET_LOADING_BOM_TYPE_SEQUENCE: 'BOM_V2/SET_LOADING_BOM_TYPE_SEQUENCE',
  SET_BOM_TYPE_SEQUENCE_LIST: 'BOM_V2/SET_BOM_TYPE_SEQUENCE_LIST',
  SET_LOADING_BOM_LIBRARIES: 'BOM_V2/SET_LOADING_BOM_LIBRARIES',
  SET_BOM_LIBRARIES: 'BOM_V2/SET_BOM_LIBRARIES',
  SET_PRODUCT_TYPES: 'BOM_V2/SET_PRODUCT_TYPES',
  SET_LOADING_BOM_TYPE_GROUPS: 'BOM_V2/SET_LOADING_BOM_TYPE_GROUPS',
  SET_BOM_TYPE_GROUPS: 'BOM_V2/SET_BOM_TYPE_GROUPS',
  SET_LOADING_CATEGORIES: 'BOM_V2/SET_LOADING_CATEGORIES',
  SET_CATEGORIES: 'BOM_V2/SET_CATEGORIES',
  SET_SEARCHING_BOM_LIBRARIES: 'BOM_V2/SET_SEARCHING_BOM_LIBRARIES',
  SET_BOM_LIBRARY_SEARCH_RESULTS: 'BOM_V2/SET_BOM_LIBRARY_SEARCH_RESULTS',
  SET_LOADING_BOM_LIBRARY_CARDS: 'BOM_V2/SET_LOADING_BOM_LIBRARY_CARDS',
  SET_BOM_LIBRARY_CARDS: 'BOM_V2/SET_BOM_LIBRARY_CARDS',
  SET_LOADING_BOM_LIBRARY_DETAIL: 'BOM_V2/SET_LOADING_BOM_LIBRARY_DETAIL',
  SET_BOM_LIBRARY_DETAIL: 'BOM_V2/SET_BOM_LIBRARY_DETAIL',
  SET_BOM_LIBRARY_TAGS: 'BOM_V2/SET_BOM_LIBRARY_TAGS',
  SET_BOM_LIBRARY_FILTER_OPTIONS: 'BOM_V2/SET_BOM_LIBRARY_FILTER_OPTIONS',
  SET_LOADING_BOM_LIBRARY_GROUPS: 'BOM_V2/SET_LOADING_BOM_LIBRARY_GROUPS',
  SET_BOM_LIBRARY_GROUPS: 'BOM_V2/SET_BOM_LIBRARY_GROUPS',
  SET_SEASONS: 'BOM_V2/SET_SEASONS',
  SET_ERROR: 'BOM_V2/SET_ERROR',
} as const;

export type BomV2Action =
  | { type: typeof BOM_V2_ACTION_TYPES.SET_LOADING_BOM_TYPES; payload: boolean }
  | { type: typeof BOM_V2_ACTION_TYPES.SET_BOM_TYPES; payload: BomV2PagePayload<BomTypeItem> }
  | { type: typeof BOM_V2_ACTION_TYPES.SET_LOADING_BOM_TYPE_SEQUENCE; payload: boolean }
  | { type: typeof BOM_V2_ACTION_TYPES.SET_BOM_TYPE_SEQUENCE_LIST; payload: BomTypeItem[] }
  | { type: typeof BOM_V2_ACTION_TYPES.SET_LOADING_BOM_LIBRARIES; payload: boolean }
  | { type: typeof BOM_V2_ACTION_TYPES.SET_BOM_LIBRARIES; payload: BomV2PagePayload<BomLibraryItem> }
  | { type: typeof BOM_V2_ACTION_TYPES.SET_PRODUCT_TYPES; payload: BomV2Ref[] }
  | { type: typeof BOM_V2_ACTION_TYPES.SET_LOADING_BOM_TYPE_GROUPS; payload: boolean }
  | { type: typeof BOM_V2_ACTION_TYPES.SET_BOM_TYPE_GROUPS; payload: BomV2PagePayload<BomTypeGroupItem> }
  | { type: typeof BOM_V2_ACTION_TYPES.SET_LOADING_CATEGORIES; payload: boolean }
  | { type: typeof BOM_V2_ACTION_TYPES.SET_CATEGORIES; payload: { categories: CategoryV2Item[]; catBasedOnPT: boolean } }
  | { type: typeof BOM_V2_ACTION_TYPES.SET_SEARCHING_BOM_LIBRARIES; payload: boolean }
  | { type: typeof BOM_V2_ACTION_TYPES.SET_BOM_LIBRARY_SEARCH_RESULTS; payload: BomLibraryCardItem[] }
  | { type: typeof BOM_V2_ACTION_TYPES.SET_LOADING_BOM_LIBRARY_CARDS; payload: boolean }
  | { type: typeof BOM_V2_ACTION_TYPES.SET_BOM_LIBRARY_CARDS; payload: BomV2PagePayload<BomLibraryCardItem> }
  | { type: typeof BOM_V2_ACTION_TYPES.SET_LOADING_BOM_LIBRARY_DETAIL; payload: boolean }
  | { type: typeof BOM_V2_ACTION_TYPES.SET_BOM_LIBRARY_DETAIL; payload: BomLibraryDetailItem | null }
  | { type: typeof BOM_V2_ACTION_TYPES.SET_BOM_LIBRARY_TAGS; payload: string[] }
  | { type: typeof BOM_V2_ACTION_TYPES.SET_BOM_LIBRARY_FILTER_OPTIONS; payload: BomLibraryFilterOptions }
  | { type: typeof BOM_V2_ACTION_TYPES.SET_LOADING_BOM_LIBRARY_GROUPS; payload: boolean }
  | { type: typeof BOM_V2_ACTION_TYPES.SET_BOM_LIBRARY_GROUPS; payload: BomV2PagePayload<BomLibraryGroupItem> }
  | { type: typeof BOM_V2_ACTION_TYPES.SET_SEASONS; payload: BomV2Ref[] }
  | { type: typeof BOM_V2_ACTION_TYPES.SET_ERROR; payload: string | null };

export type BomV2ReducerAction = BomV2Action | { type: string };

const BOM_V2_ACTION_TYPE_SET: ReadonlySet<string> = new Set(Object.values(BOM_V2_ACTION_TYPES));

/**
 * Narrows a reducer action to the BOM action union.
 * @param {BomV2ReducerAction} action - Any dispatched action.
 * @returns {boolean} True when the action belongs to this slice.
 */
export const isBomV2Action = (action: BomV2ReducerAction): action is BomV2Action => BOM_V2_ACTION_TYPE_SET.has(action.type);
